// HighlightMatch.tsx — case-insensitive substring highlight for palette
// search results.
//
// Scope: server-result groups (workflow/engine_ref/execution/user/audit/
// tenant) + Navigation only, per the palette spec. Actions/Recent/Context
// are intentionally left unhighlighted — those are fixed command labels, not
// search matches, and highlighting them would just be visual noise.
//
// Prefers the API's `highlights[]` for the given field (the backend knows
// exactly which substring it matched on, which may differ from a naive
// client-side indexOf — e.g. normalization, synonyms); falls back to a local
// case-insensitive indexOf against the live query when the API didn't send
// a highlight for this field (also how Navigation entries — which have no
// server highlights at all — are highlighted).
//
// Deliberately NOT features/search/components/SearchHighlight.tsx: that
// component parses a different wire shape (`<em>` markup inside
// highlight strings) and renders a yellow <mark>, which is the OLD
// /tenant/search (SearchBar/SearchPage) contract's design language. The
// palette spec calls for a text-primary-600/dark:text-primary-400
// semibold span instead, so this is a small purpose-built sibling rather
// than a reuse of that out-of-scope component.

import type { GlobalSearchHighlight } from './globalSearchApi'

interface HighlightMatchProps {
  text: string
  /** Live query string — used when no server highlight matches `field`. */
  query: string
  /** This item's field name (e.g. 'title', 'subtitle') to look up in highlights. */
  field: string
  highlights?: GlobalSearchHighlight[]
  className?: string
}

export function HighlightMatch({ text, query, field, highlights, className }: HighlightMatchProps) {
  const serverNeedle = highlights?.find((h) => h.field === field)?.value
  const needle = (serverNeedle ?? query).trim()

  if (!needle || !text) {
    return <span className={className}>{text}</span>
  }

  const idx = text.toLowerCase().indexOf(needle.toLowerCase())
  if (idx === -1) {
    return <span className={className}>{text}</span>
  }

  return (
    <span className={className}>
      {text.slice(0, idx)}
      <span className="text-primary-600 dark:text-primary-400 font-semibold">
        {text.slice(idx, idx + needle.length)}
      </span>
      {text.slice(idx + needle.length)}
    </span>
  )
}
